/**
 * Set Default Identity Modal
 *
 * Confirmation modal for setting a billing identity as the default.
 */

import { Warning as WarningIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import type { BillingEntityResponse } from '@store/generated/billing-entities';

interface SetDefaultIdentityModalProps {
  open: boolean;
  entity: BillingEntityResponse | null;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
}

export function SetDefaultIdentityModal({
  open,
  entity,
  onClose,
  onConfirm,
  isLoading,
}: SetDefaultIdentityModalProps) {
  if (!entity) return null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth={"sm"}>
      <DialogTitle>Set Default Billing Identity?</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
          <WarningIcon color="warning" sx={{ mt: 0.5 }} />
          <Box>
            <Typography variant="body1" gutterBottom>
              All claims will be submitted using your default billing identity. Choose carefully.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }} component="p">
              Setting <strong>{entity.legal_name}</strong> as the default will automatically use
              this NPI and Tax ID for all future claim submissions.
            </Typography>
            <Box
              sx={{
                p: 2,
                bgcolor: 'background.default',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                NPI: {entity.billing_npi}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                Tax ID: {entity.tax_id_masked} ({entity.tax_id_type})
              </Typography>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button onClick={onConfirm} variant="contained" disabled={isLoading}>
          {isLoading ? 'Setting...' : 'Set as Default'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
